import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../api';
import ContactForm, { type ContactFormData } from '../../components/ContactForm';
import { logger } from '../../utils/logger';
import { colors, spacing, radius, fontSize } from './styles';

type TabId = 'general' | 'subscription' | 'security' | 'notifications' | 'data' | 'contacts';

interface SubscriptionStatus {
  plan: string;
  status: 'active' | 'cancelled' | 'expired' | 'free';
  nextBillingDate: string | null;
  confirmationUrl: string | null;
}

interface SubscriptionCreateResponse {
  success: boolean;
  data: {
    confirmation_url: string;
    subscriptionId: string;
  };
}

interface Contact {
  id: string;
  name: string;
  birthDate: string;
  birthTime?: string;
  birthPlace: { city?: string; country?: string; latitude: number; longitude: number };
  birthPlaceFull?: string;
  relationshipType?: string;
  notes?: string;
}

/* ─── reusable style objects ─── */
const labelStyle: React.CSSProperties = {
  display: 'block',
  color: '#ffffff',
  fontWeight: 500,
  marginBottom: '6px',
  fontSize: '14px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px',
  background: 'rgba(255, 255, 255, 0.08)',
  border: '1px solid rgba(212, 175, 55, 0.3)',
  borderRadius: '10px',
  color: '#ffffff',
  fontSize: '15px',
  boxSizing: 'border-box',
  outline: 'none',
};

const cardStyle: React.CSSProperties = {
  background: colors.bgCard,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.lg,
  padding: spacing.xl,
};

const btnPrimary: React.CSSProperties = {
  background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.goldLight} 100%)`,
  border: 'none',
  padding: '12px 30px',
  borderRadius: radius.md,
  color: colors.bg,
  fontSize: fontSize.xl,
  fontWeight: 'bold',
  cursor: 'pointer',
};

const btnDanger: React.CSSProperties = {
  background: 'rgba(220, 53, 69, 0.1)',
  border: `1px solid rgba(220, 53, 69, 0.3)`,
  padding: '8px 20px',
  borderRadius: radius.sm,
  color: colors.error,
  fontSize: fontSize.sm,
  cursor: 'pointer',
};

const btnSecondary: React.CSSProperties = {
  background: 'rgba(212, 175, 55, 0.1)',
  border: `1px solid ${colors.border}`,
  padding: '12px 25px',
  borderRadius: radius.md,
  color: colors.gold,
  fontSize: fontSize.xl,
  cursor: 'pointer',
};

/* ─── component ─── */
const ProfilePage: React.FC = () => {
  const { user, refreshAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeTab, setActiveTab] = useState<TabId>('general');

  // Force general tab on mount
  useEffect(() => {
    setActiveTab('general');
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Read ?tab= param
  useEffect(() => {
    const tab = searchParams.get('tab') as TabId;
    const validTabs: TabId[] = ['general', 'subscription', 'contacts', 'security', 'notifications', 'data'];
    if (tab && validTabs.includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // ── contacts ──
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [contactSaving, setContactSaving] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);

  // ── delete modal ──
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // ── general form ──
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    birth_date: '',
    birth_time: '',
    birth_place: '',
    gender: '',
    bio: '',
  });

  // ── notifications ──
  const [notifications, setNotifications] = useState({
    email_forecasts: true,
    email_transits: true,
    email_news: false,
    email_promo: false,
    email_moon_events: true,
    email_retrograde: true,
    email_weekly_digest: false,
    telegram_enabled: false,
    telegram_chat_id: '',
  });
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);

  // ── subscription ──
  const [subLoading, setSubLoading] = useState(false);
  const [subStatus, setSubStatus] = useState<SubscriptionStatus | null>(null);

  // ── security ──
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [activeSessions] = useState([
    { id: '1', device: 'Текущая сессия', browser: 'Этот браузер', active: true },
  ]);

  // ── data export ──
  const [exportLoading, setExportLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Populate form from user object
  useEffect(() => {
    if (user) {
      const birthDate = user.birth_date
        ? user.birth_date.includes('T') ? user.birth_date.split('T')[0] : user.birth_date
        : '';

      setFormData({
        full_name: user.full_name || '',
        email: user.email || '',
        birth_date: birthDate,
        birth_time: user.birth_time || '',
        birth_place: user.birth_place || '',
        gender: user.gender || '',
        bio: user.bio || '',
      });
    }
  }, [user]);

  // Load notification settings from server
  useEffect(() => {
    let cancelled = false;
    const loadNotificationSettings = async () => {
      setNotifLoading(true);
      try {
        const res = await api.get<{ success: boolean; data: { notification_settings: typeof notifications } }>('/profile');
        if (!cancelled && res.success && res.data?.notification_settings) {
          const settings = res.data.notification_settings;
          setNotifications({
            email_forecasts: settings.email_forecasts ?? true,
            email_transits: settings.email_transits ?? true,
            email_news: settings.email_news ?? false,
            email_promo: settings.email_promo ?? false,
            email_moon_events: settings.email_moon_events ?? true,
            email_retrograde: settings.email_retrograde ?? true,
            email_weekly_digest: settings.email_weekly_digest ?? false,
            telegram_enabled: settings.telegram_enabled ?? false,
            telegram_chat_id: settings.telegram_chat_id || '',
          });
        }
      } catch (e) {
        logger.error('loadNotificationSettings error', e);
        // Silently keep defaults
      } finally {
        if (!cancelled) setNotifLoading(false);
      }
    };
    if (user) {
      loadNotificationSettings();
    }
    return () => { cancelled = true; };
  }, [user]);

  // Load sub/contacts when tab opens
  useEffect(() => {
    if (activeTab === 'subscription') fetchSubscriptionStatus();
    if (activeTab === 'contacts') loadContacts();
  }, [activeTab]);

  const loadContacts = useCallback(async () => {
    setContactsLoading(true);
    try {
      const res = await api.get<Contact[]>('/calculations/contacts');
      if (res.success && res.data) setContacts(res.data);
    } catch (e) {
      logger.error('loadContacts error', e);
    }
    setContactsLoading(false);
  }, []);

  const handleSaveContact = async (data: ContactFormData) => {
    setContactSaving(true);
    setContactError(null);
    try {
      if (editingContact) {
        const res = await api.put(`/calculations/contacts/${editingContact.id}`, data);
        if (res.success) {
          setShowContactForm(false);
          setEditingContact(null);
          loadContacts();
        } else {
          setContactError(res.error || 'Ошибка сохранения');
        }
      } else {
        const res = await api.post<Contact>('/calculations/contacts', data);
        if (res.success && res.data) {
          setShowContactForm(false);
          loadContacts();
        } else {
          setContactError(res.error || 'Ошибка сохранения');
        }
      }
    } catch {
      setContactError('Ошибка соединения');
    } finally {
      setContactSaving(false);
    }
  };

  const handleDeleteContact = async (contactId: string) => {
    if (!confirm('Удалить этот контакт?')) return;
    const res = await api.delete(`/calculations/contacts/${contactId}`);
    if (res.success) setContacts((prev) => prev.filter((c) => c.id !== contactId));
  };

  const openEditContact = (contact: Contact) => {
    setEditingContact(contact);
    setShowContactForm(true);
    setContactError(null);
  };

  const openCreateContact = () => {
    setEditingContact(null);
    setShowContactForm(true);
    setContactError(null);
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const res = await api.get<{ success: boolean; data: SubscriptionStatus }>('/subscription/status');
      if (res.success && res.data) setSubStatus(res.data);
    } catch {
      // silently ignore — show status from AuthContext
    }
  };

  /* ── general save ── */
  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const res = await api.put('/profile', {
        full_name: formData.full_name,
        birth_date: formData.birth_date,
        birth_time: formData.birth_time || null,
        birth_place: formData.birth_place,
        gender: formData.gender || null,
        bio: formData.bio || null,
      });
      if (res.success) {
        const natalChanged = formData.birth_date !== (user?.birth_date?.includes('T') ? user.birth_date.split('T')[0] : user?.birth_date) ||
          formData.birth_time !== (user?.birth_time || '') ||
          formData.birth_place !== (user?.birth_place || '');
        
        if (natalChanged) {
          setMessage('Изменения сохранены. Натальная карта будет пересчитана при следующем запросе.');
        } else {
          setMessage('Изменения сохранены');
        }
        await refreshAuth();
      } else {
        setMessage(res.error || 'Ошибка сохранения');
      }
    } catch {
      setMessage('Ошибка соединения');
    } finally {
      setSaving(false);
    }
  };

  /* ── subscription ── */
  const handleCreateSubscription = async (planType: 'monthly' | 'yearly') => {
    setSubLoading(true);
    setMessage('');
    try {
      const res = await api.post<SubscriptionCreateResponse>('/subscription/create', { planType });
      if (res.success && res.data?.confirmation_url) {
        window.location.href = res.data.confirmation_url;
      } else {
        setMessage(res.error || 'Не удалось создать подписку');
      }
    } catch {
      setMessage('Ошибка соединения');
    } finally {
      setSubLoading(false);
    }
  };

  const handleCancelSubscription = async (subscriptionId: string) => {
    if (!confirm('Вы уверены, что хотите отменить подписку?')) return;
    setSubLoading(true);
    setMessage('');
    try {
      const res = await api.post('/subscription/cancel', { subscriptionId });
      if (res.success) {
        setMessage('Подписка отменена');
        fetchSubscriptionStatus();
        refreshAuth();
      } else {
        setMessage(res.error || 'Не удалось отменить подписку');
      }
    } catch {
      setMessage('Ошибка соединения');
    } finally {
      setSubLoading(false);
    }
  };

  /* ── security / change password ── */
  const handleChangePassword = async () => {
    setMessage('');
    if (passwordData.new_password !== passwordData.confirm_password) {
      setMessage('Пароли не совпадают');
      return;
    }
    if (passwordData.new_password.length < 8) {
      setMessage('Новый пароль должен содержать минимум 8 символов');
      return;
    }

    setPasswordLoading(true);
    try {
      const res = await api.post('/auth/change-password', {
        old_password: passwordData.old_password,
        new_password: passwordData.new_password,
      });
      if (res.success) {
        setMessage('Пароль успешно изменён');
        setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
      } else {
        setMessage(res.error || 'Ошибка смены пароля');
      }
    } catch {
      setMessage('Ошибка соединения');
    } finally {
      setPasswordLoading(false);
    }
  };

  /* ── notifications save ── */
  const handleSaveNotifications = async () => {
    setNotifSaving(true);
    setMessage('');
    try {
      const res = await api.put('/profile', {
        user_preferences: { notifications },
      });
      if (res.success) {
        setMessage('Настройки уведомлений сохранены');
        await refreshAuth();
      } else {
        setMessage(res.error || 'Ошибка сохранения настроек уведомлений');
      }
    } catch (e) {
      logger.error('handleSaveNotifications error', e);
      setMessage('Ошибка соединения при сохранении уведомлений');
    } finally {
      setNotifSaving(false);
    }
  };

  /* ── data export ── */
  const handleExportData = async () => {
    setExportLoading(true);
    setMessage('');
    try {
      const baseUrl = import.meta.env.VITE_API_URL || '/api';
      const res = await fetch(`${baseUrl}/export/all`, {
        credentials: 'include', // HttpOnly cookies (фаза 1.1)
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `astranum-data-${user?.id || 'export'}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        setMessage('Данные экспортированы');
      } else {
        setMessage('Не удалось экспортировать данные');
      }
    } catch {
      setMessage('Ошибка соединения');
    } finally {
      setExportLoading(false);
    }
  };

  /* ── delete account ── */
  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'УДАЛИТЬ') {
      setMessage('Введите «УДАЛИТЬ» для подтверждения');
      return;
    }

    setDeleteLoading(true);
    setMessage('');
    try {
      const res = await api.delete('/auth/account');
      if (res.success) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = '/';
      } else {
        setMessage(res.error || 'Не удалось удалить аккаунт');
      }
    } catch {
      setMessage('Ошибка соединения');
    } finally {
      setDeleteLoading(false);
    }
  };

  const openDeleteModal = () => {
    setDeleteConfirmText('');
    setShowDeleteModal(true);
  };

  /* ── tabs config ── */
  const tabs: { id: TabId; icon: string; label: string }[] = [
    { id: 'general', icon: '👤', label: 'Общее' },
    { id: 'subscription', icon: '💎', label: 'Подписка' },
    { id: 'contacts', icon: '👥', label: 'Контакты' },
    { id: 'security', icon: '🔒', label: 'Безопасность' },
    { id: 'notifications', icon: '🔔', label: 'Уведомления' },
    { id: 'data', icon: '📁', label: 'Данные' },
  ];

  const currentPlan = subStatus?.plan || user?.subscription_status || 'free';

  const isErrorMsg = (msg: string) =>
    msg.includes('Ошибка') || msg.includes('не совпадают') || msg.includes('не удалось') || msg.includes('Неверный') || msg.includes('Введите');

  /* ─── render ─── */
  return (
    <div style={{ minHeight: '100vh', color: colors.text, fontFamily: 'serif', position: 'relative', zIndex: 1 }}>
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: isMobile ? '20px 12px' : '30px 20px' }}>
        <h1 style={{
          fontFamily: 'var(--font-display)',
          fontStyle: 'italic',
          fontSize: isMobile ? '24px' : fontSize.heading,
          fontWeight: 'bold',
          background: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 40%, #B8942B 70%, #F4E4A6 100%)',
          backgroundSize: '200% auto',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          marginBottom: spacing.xl,
        }}>
          Настройки профиля
        </h1>

        {/* Tabs */}
        <div style={{
          display: 'flex', gap: '4px', marginBottom: spacing.xl,
          borderBottom: `1px solid ${colors.border}`,
          overflowX: 'auto',
        }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: spacing.xs,
                padding: '12px 20px',
                background: activeTab === tab.id ? 'rgba(212, 175, 55, 0.15)' : 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.id ? `2px solid ${colors.gold}` : '2px solid transparent',
                color: activeTab === tab.id ? colors.gold : colors.textMuted,
                fontSize: fontSize.md, cursor: 'pointer',
                transition: 'all 0.3s', whiteSpace: 'nowrap',
              }}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Message banner */}
        {message && (
          <div style={{
            background: isErrorMsg(message) ? 'rgba(220, 53, 69, 0.2)' : 'rgba(40, 167, 69, 0.2)',
            border: `1px solid ${isErrorMsg(message) ? colors.error : colors.success}`,
            color: isErrorMsg(message) ? colors.error : colors.success,
            padding: '12px', borderRadius: radius.md, marginBottom: spacing.md, fontSize: fontSize.md,
          }}>
            {message}
          </div>
        )}

        {/* Card container */}
        <div style={{
          ...cardStyle,
          padding: isMobile ? '20px 16px' : spacing.xl,
          boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />

          {/* ════════════════════════════════════════ GENERAL ════════════════════════════════════════ */}
          {activeTab === 'general' && (
            <div>
              <h2 style={{ color: '#f0f0f0', fontSize: isMobile ? '18px' : fontSize.title, marginBottom: spacing.lg }}>Общая информация</h2>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.lg, marginBottom: spacing.lg }}>
                <div>
                  <label style={labelStyle}>Email</label>
                  <input type="email" value={formData.email} disabled style={{ ...inputStyle, opacity: 0.5 }} />
                </div>
                <div>
                  <label style={labelStyle}>Полное имя</label>
                  <input type="text" value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Дата рождения</label>
                  <input type="date" value={formData.birth_date}
                    onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                    style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Время рождения</label>
                  <input type="time" value={formData.birth_time}
                    onChange={(e) => setFormData({ ...formData, birth_time: e.target.value })}
                    style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Место рождения</label>
                  <input type="text" value={formData.birth_place}
                    onChange={(e) => setFormData({ ...formData, birth_place: e.target.value })}
                    style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Пол</label>
                  <select value={formData.gender}
                    onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                    style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value="" style={{ background: colors.bg }}>Не указан</option>
                    <option value="male" style={{ background: colors.bg }}>Мужской</option>
                    <option value="female" style={{ background: colors.bg }}>Женский</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: spacing.lg }}>
                <label style={labelStyle}>О себе</label>
                <textarea value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  placeholder="Расскажите о себе..."
                  rows={4}
                  style={{ ...inputStyle, resize: 'vertical', fontFamily: 'serif' }} />
              </div>

              <button onClick={handleSave} disabled={saving} style={{
                ...btnPrimary,
                background: saving ? '#666' : btnPrimary.background,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}>
                {saving ? 'Сохранение...' : 'Сохранить'}
              </button>

              {/* Расширенные данные */}
              <div style={{
                marginTop: spacing.lg,
                padding: spacing.lg,
                background: 'rgba(212, 175, 55, 0.06)',
                border: `1px solid ${colors.gold}`,
                borderRadius: radius.md,
              }}>
                <button
                  onClick={() => navigate('/profile/extended')}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: spacing.md,
                    width: '100%',
                    textAlign: 'left',
                  }}
                >
                  <span style={{ fontSize: '24px', flexShrink: 0 }}>📋</span>
                  <div>
                    <div style={{ color: colors.gold, fontSize: fontSize.lg, fontWeight: 'bold', marginBottom: '4px' }}>
                      Расширенные данные
                    </div>
                    <div style={{ color: colors.textMuted, fontSize: fontSize.md, lineHeight: '1.5' }}>
                      Управляйте адресами, телефонами, детьми, питомцами
                    </div>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* ════════════════════════════════════════ SUBSCRIPTION ════════════════════════════════════════ */}
          {activeTab === 'subscription' && (
            <div>
              <h2 style={{ color: '#f0f0f0', fontSize: isMobile ? '18px' : fontSize.title, marginBottom: spacing.lg }}>Подписка</h2>

              {/* Current status */}
              <div style={{
                background: 'rgba(212, 175, 55, 0.08)',
                border: `1px solid ${colors.border}`,
                borderRadius: radius.md, padding: spacing.lg, marginBottom: spacing.lg,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md }}>
                  <div>
                    <div style={{ color: colors.gold, fontSize: '24px', fontWeight: 'bold', textTransform: 'uppercase' }}>
                      {currentPlan}
                    </div>
                    <div style={{ color: colors.textMuted, fontSize: fontSize.md, marginTop: '4px' }}>
                      Текущий тариф
                    </div>
                    {subStatus?.nextBillingDate && (
                      <div style={{ color: '#aaa', fontSize: fontSize.sm, marginTop: '4px' }}>
                        Следующее списание: {new Date(subStatus.nextBillingDate).toLocaleDateString('ru-RU')}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: '48px' }}>
                    {currentPlan === 'vip' ? '💎' : currentPlan === 'premium' ? '⭐' : '🆓'}
                  </div>
                </div>

                {currentPlan !== 'free' && subStatus?.status === 'active' && (
                  <button
                    onClick={() => handleCancelSubscription(currentPlan)}
                    disabled={subLoading}
                    style={{
                      ...btnDanger,
                      cursor: subLoading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {subLoading ? 'Обработка...' : 'Отменить подписку'}
                  </button>
                )}
              </div>

              {/* Plans */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: spacing.lg }}>
                {[
                  { plan: 'Free', price: '0 ₽', features: ['Базовые расчёты', 'Нумерология', 'Ограниченные прогнозы'], key: 'free' as const },
                  { plan: 'Premium', price: '690 ₽/мес', features: ['Все расчёты', 'Полные прогнозы', 'Натальная карта', 'Приоритет'], key: 'monthly' as const, popular: true },
                  { plan: 'Premium (год)', price: '590 ₽/мес', features: ['Всё из Premium', 'Скидка 15%', 'Экономия 1200 ₽/год'], key: 'yearly' as const },
                ].map((item) => {
                  const isCurrent = currentPlan === item.key || (item.key === 'free' && currentPlan === 'free');
                  return (
                    <div key={item.plan} style={{
                      display: 'flex', flexDirection: 'column',
                      minHeight: '320px',
                      background: isCurrent ? 'rgba(212, 175, 55, 0.1)' : colors.bgCard,
                      border: `1px solid ${isCurrent ? colors.gold : 'rgba(212, 175, 55, 0.15)'}`,
                      borderRadius: radius.md, padding: spacing.lg, position: 'relative',
                    }}>
                      {item.popular && (
                        <div style={{
                          position: 'absolute', top: '-10px', right: '15px',
                          background: colors.gold, color: colors.bg,
                          padding: '2px 10px', borderRadius: radius.sm, fontSize: '11px', fontWeight: 'bold',
                        }}>
                          Популярный
                        </div>
                      )}
                      <div style={{ color: colors.gold, fontSize: fontSize.lg, fontWeight: 'bold', marginBottom: '4px' }}>{item.plan}</div>
                      <div style={{ color: colors.text, fontSize: '22px', fontWeight: 'bold', marginBottom: '15px' }}>{item.price}</div>
                      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', flex: 1 }}>
                        {item.features.map((f, i) => (
                          <li key={i} style={{ color: '#aaa', fontSize: fontSize.sm, padding: '4px 0' }}>✓ {f}</li>
                        ))}
                      </ul>
                      <button
                        onClick={() => {
                          if (item.key === 'monthly' || item.key === 'yearly') handleCreateSubscription(item.key);
                        }}
                        disabled={isCurrent || subLoading}
                        style={{
                          width: '100%', padding: '10px',
                          background: isCurrent ? 'rgba(212, 175, 55, 0.2)' : `linear-gradient(135deg, ${colors.gold} 0%, ${colors.goldLight} 100%)`,
                          border: 'none', borderRadius: radius.sm,
                          color: isCurrent ? colors.gold : colors.bg,
                          fontSize: fontSize.md, fontWeight: 'bold',
                          cursor: isCurrent || subLoading ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {subLoading ? 'Обработка...' : isCurrent ? 'Текущий' : 'Выбрать'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ════════════════════════════════════════ CONTACTS ════════════════════════════════════════ */}
          {activeTab === 'contacts' && (
            <div>
              {showContactForm ? (
                <ContactForm
                  contact={editingContact}
                  mode={editingContact ? 'edit' : 'create'}
                  onSave={handleSaveContact}
                  onCancel={() => { setShowContactForm(false); setEditingContact(null); setContactError(null); }}
                  loading={contactSaving}
                  error={contactError}
                />
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.lg }}>
                    <div>
                      <h2 style={{ color: '#f0f0f0', fontSize: isMobile ? '18px' : fontSize.title, marginBottom: '4px' }}>Мои контакты</h2>
                      <p style={{ color: colors.textMuted, fontSize: fontSize.md, margin: 0, lineHeight: '1.6' }}>
                        Контакты для расчёта совместимости ({contacts.length})
                      </p>
                    </div>
                    <button onClick={openCreateContact} style={{
                      ...btnPrimary, fontSize: fontSize.md, padding: '10px 20px',
                    }}>
                      + Добавить
                    </button>
                  </div>

                  {contactsLoading ? (
                    <div style={{ color: colors.textMuted, textAlign: 'center', padding: spacing.xl }}>Загрузка...</div>
                  ) : contacts.length === 0 ? (
                    <div style={{
                      background: colors.bgCard, border: `1px solid ${colors.border}`,
                      borderRadius: radius.md, padding: '40px', textAlign: 'center',
                    }}>
                      <div style={{ fontSize: '36px', marginBottom: '12px' }}>...</div>
                      <p style={{ color: colors.textMuted, fontSize: fontSize.md }}>У вас пока нет контактов</p>
                      <button onClick={openCreateContact} style={{ ...btnPrimary, fontSize: fontSize.md, marginTop: spacing.md }}>
                        Добавить первый контакт
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gap: spacing.md }}>
                      {contacts.map((c) => (
                        <div key={c.id} style={{
                          background: colors.bgCardHover, border: `1px solid ${colors.border}`,
                          borderRadius: radius.md, padding: spacing.md,
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}>
                          <div>
                            <div style={{ color: colors.text, fontWeight: 500, fontSize: fontSize.lg }}>{c.name}</div>
                            <div style={{ color: colors.textMuted, fontSize: fontSize.sm }}>
                              📅 {new Date(c.birthDate).toLocaleDateString('ru-RU')}
                              {c.birthPlace?.city && ` · 📍 ${c.birthPlace.city}`}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: spacing.sm }}>
                            <button onClick={() => openEditContact(c)} style={{
                              ...btnSecondary, padding: '6px 14px', fontSize: fontSize.sm,
                            }}>
                              ✎
                            </button>
                            <button onClick={() => handleDeleteContact(c.id)} style={{
                              ...btnDanger, padding: '6px 14px', fontSize: fontSize.sm,
                            }}>
                              ✕
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ════════════════════════════════════════ SECURITY ════════════════════════════════════════ */}
          {activeTab === 'security' && (
            <div>
              <h2 style={{ color: '#f0f0f0', fontSize: isMobile ? '18px' : fontSize.title, marginBottom: spacing.lg }}>Безопасность</h2>

              {/* Change password */}
              <div style={{ marginBottom: spacing.lg }}>
                <h3 style={{ color: colors.text, fontSize: fontSize.lg, marginBottom: spacing.md }}>Смена пароля</h3>
                <div style={{ display: 'grid', gap: spacing.md, maxWidth: '400px' }}>
                  <div>
                    <label style={labelStyle}>Текущий пароль</label>
                    <input type="password" value={passwordData.old_password}
                      onChange={(e) => setPasswordData({ ...passwordData, old_password: e.target.value })}
                      style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Новый пароль</label>
                    <input type="password" value={passwordData.new_password}
                      onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                      style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Подтвердите пароль</label>
                    <input type="password" value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                      style={inputStyle} />
                  </div>
                  <button onClick={handleChangePassword} disabled={passwordLoading} style={{
                    ...btnPrimary,
                    cursor: passwordLoading ? 'not-allowed' : 'pointer',
                  }}>
                    {passwordLoading ? 'Обработка...' : 'Изменить пароль'}
                  </button>
                </div>
              </div>

              {/* Active sessions */}
              <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: spacing.lg }}>
                <h3 style={{ color: colors.text, fontSize: fontSize.lg, marginBottom: spacing.md }}>Активные сессии</h3>
                {activeSessions.map((s) => (
                  <div key={s.id} style={{
                    background: colors.bgCardHover, border: `1px solid ${colors.border}`,
                    borderRadius: radius.md, padding: spacing.md, marginBottom: spacing.sm,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <div>
                      <div style={{ color: colors.text, fontWeight: 500, fontSize: fontSize.md }}>{s.device}</div>
                      <div style={{ color: colors.textMuted, fontSize: fontSize.sm }}>{s.browser}</div>
                    </div>
                    {s.active && <span style={{ color: colors.success, fontSize: fontSize.sm }}>● Активна</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ════════════════════════════════════════ NOTIFICATIONS ════════════════════════════════════════ */}
          {activeTab === 'notifications' && (
            <div>
              <h2 style={{ color: '#f0f0f0', fontSize: isMobile ? '18px' : fontSize.title, marginBottom: spacing.lg }}>Уведомления</h2>

              {notifLoading ? (
                <div style={{ color: colors.textMuted, textAlign: 'center', padding: spacing.xl }}>Загрузка настроек...</div>
              ) : (
              <>
              <div style={{ display: 'grid', gap: spacing.md }}>
                {(Object.keys(notifications) as (keyof typeof notifications)[]).filter((k) => k.startsWith('email_')).map((key) => {
                  const labels: Record<string, string> = {
                    email_forecasts: 'Прогнозы по email',
                    email_transits: 'Транзиты по email',
                    email_news: 'Новости',
                    email_promo: 'Акции и предложения',
                    email_moon_events: 'Лунные события',
                    email_retrograde: 'Ретроградность планет',
                    email_weekly_digest: 'Еженедельный дайджест',
                  };
                  return (
                    <div key={key} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 0', borderBottom: `1px solid rgba(255,255,255,0.05)`,
                    }}>
                      <span style={{ color: colors.text, fontSize: fontSize.md }}>{labels[key] || key}</span>
                      <label style={{ position: 'relative', width: '48px', height: '26px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={notifications[key] as boolean}
                          onChange={(e) => setNotifications({ ...notifications, [key]: e.target.checked })}
                          style={{ display: 'none' }}
                        />
                        <span style={{
                          position: 'absolute', inset: 0,
                          background: (notifications[key] as boolean) ? colors.gold : 'rgba(255,255,255,0.15)',
                          borderRadius: '13px', transition: 'background 0.2s',
                        }} />
                        <span style={{
                          position: 'absolute', top: '3px',
                          left: (notifications[key] as boolean) ? '25px' : '3px',
                          width: '20px', height: '20px', background: '#fff', borderRadius: '50%',
                          transition: 'left 0.2s',
                        }} />
                      </label>
                    </div>
                  );
                })}
              </div>

              {/* Telegram */}
              <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: spacing.lg, marginTop: spacing.lg }}>
                <h3 style={{ color: colors.text, fontSize: fontSize.lg, marginBottom: spacing.md }}>Telegram-уведомления</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md, marginBottom: spacing.md }}>
                  <label style={{ position: 'relative', width: '48px', height: '26px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={notifications.telegram_enabled}
                      onChange={(e) => setNotifications({ ...notifications, telegram_enabled: e.target.checked })}
                      style={{ display: 'none' }}
                    />
                    <span style={{
                      position: 'absolute', inset: 0,
                      background: notifications.telegram_enabled ? colors.gold : 'rgba(255,255,255,0.15)',
                      borderRadius: '13px', transition: 'background 0.2s',
                    }} />
                    <span style={{
                      position: 'absolute', top: '3px',
                      left: notifications.telegram_enabled ? '25px' : '3px',
                      width: '20px', height: '20px', background: '#fff', borderRadius: '50%',
                      transition: 'left 0.2s',
                    }} />
                  </label>
                  <span style={{ color: colors.text, fontSize: fontSize.md }}>Включить Telegram</span>
                </div>
                {notifications.telegram_enabled && (
                  <div>
                    <label style={labelStyle}>Telegram chat ID</label>
                    <input type="text" value={notifications.telegram_chat_id}
                      onChange={(e) => setNotifications({ ...notifications, telegram_chat_id: e.target.value })}
                      placeholder="@chat_id" style={inputStyle} />
                  </div>
                )}
              </div>

              <button onClick={handleSaveNotifications} disabled={notifSaving} style={{
                ...btnPrimary,
                marginTop: spacing.lg,
                cursor: notifSaving ? 'not-allowed' : 'pointer',
              }}>
                {notifSaving ? 'Сохранение...' : 'Сохранить уведомления'}
              </button>
              </>
              )}
            </div>
          )}

          {/* ════════════════════════════════════════ DATA ════════════════════════════════════════ */}
          {activeTab === 'data' && (
            <div>
              <h2 style={{ color: '#f0f0f0', fontSize: isMobile ? '18px' : fontSize.title, marginBottom: spacing.lg }}>Данные и экспорт</h2>

              {/* Export */}
              <div style={{ marginBottom: spacing.xl }}>
                <h3 style={{ color: colors.text, fontSize: fontSize.lg, marginBottom: spacing.md }}>Экспорт данных</h3>
                <p style={{ color: colors.textMuted, fontSize: fontSize.md, marginBottom: spacing.md }}>
                  Скачайте все ваши данные в формате JSON (профиль, расчёты, контакты).
                </p>
                <button onClick={handleExportData} disabled={exportLoading} style={{
                  ...btnPrimary,
                  cursor: exportLoading ? 'not-allowed' : 'pointer',
                }}>
                  {exportLoading ? 'Подготовка...' : 'Скачать данные'}
                </button>
              </div>

              {/* Delete account */}
              <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: spacing.lg }}>
                <h3 style={{ color: colors.error, fontSize: fontSize.lg, marginBottom: spacing.md }}>Удаление аккаунта</h3>
                <p style={{ color: colors.textMuted, fontSize: fontSize.md, marginBottom: spacing.md }}>
                  Это действие необратимо. Все ваши данные, расчёты и контакты будут удалены навсегда.
                </p>
                <button onClick={openDeleteModal} style={{
                  ...btnDanger, fontSize: fontSize.md, padding: '12px 24px',
                }}>
                  Удалить аккаунт
                </button>
              </div>
            </div>
          )}

        </div>{/* /card */}
      </div>{/* /container */}

      {/* ════════════════════════════════════════ DELETE MODAL ════════════════════════════════════════ */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999,
        }} onClick={() => setShowDeleteModal(false)}>
          <div style={{
            background: colors.bg, border: `2px solid ${colors.error}`,
            borderRadius: radius.lg, padding: spacing.xl, maxWidth: '440px', width: '90%',
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: colors.error, fontSize: fontSize.title, marginBottom: spacing.md }}>
              Удаление аккаунта
            </h3>
            <p style={{ color: colors.textMuted, fontSize: fontSize.md, marginBottom: spacing.md, lineHeight: '1.6' }}>
              Это действие <strong style={{ color: colors.text }}>необратимо</strong>. Введите <strong style={{ color: colors.error }}>УДАЛИТЬ</strong> для подтверждения.
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Введите УДАЛИТЬ"
              style={{ ...inputStyle, marginBottom: spacing.lg }}
            />
            <div style={{ display: 'flex', gap: spacing.md }}>
              <button onClick={handleDeleteAccount} disabled={deleteLoading} style={{
                background: colors.error, border: 'none', padding: '12px 24px',
                borderRadius: radius.md, color: '#fff', fontSize: fontSize.md,
                fontWeight: 'bold', cursor: deleteLoading ? 'not-allowed' : 'pointer',
              }}>
                {deleteLoading ? 'Удаление...' : 'Удалить'}
              </button>
              <button onClick={() => setShowDeleteModal(false)} style={btnSecondary}>
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
